import { type AuthStateChangeEvent, AuthTokenProvider, type NormalizedAuthSession } from './authTypes'

const WORKOS_SESSION_STORAGE_KEY = 'auth.workos.session'
const WORKOS_SESSION_MUTATION_LOCK = 'admin-portal-workos-session-mutation'
const ALLOWED_ISSUERS = new Set(['https://api.workos.com', 'https://auth-api.wisprflow.com'])
const EXPIRY_SKEW_SECONDS = 30
const REFRESH_MAX_ATTEMPTS = 3
const REFRESH_RETRY_WINDOW_MS = 25_000
const REFRESH_TIMEOUT_MS = 5000

interface WorkosClaims {
  iss?: string
  sub?: string
  email?: string
  exp?: number
  client_id?: string
  org_id?: string
  name?: string
  given_name?: string
  family_name?: string
  picture?: string
  'urn:wispr:user_external_id'?: string
}

interface StoredWorkosSession {
  accessToken: string
  refreshToken: string
}

interface WorkosRefreshResponse {
  access_token?: string
  refresh_token?: string
}

interface RefreshResult {
  session: NormalizedAuthSession | null
  definitiveFailure: boolean
}

const decodeBase64Url = (value: string): string => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=')
  const binary = atob(padded)
  return decodeURIComponent(
    binary
      .split('')
      .map((character) => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`)
      .join('')
  )
}

export const decodeJWTClaims = (token: string): WorkosClaims | null => {
  const parts = token.split('.')
  if (parts.length !== 3) return null
  try {
    return JSON.parse(decodeBase64Url(parts[1])) as WorkosClaims
  } catch {
    return null
  }
}

export const decodeWorkosClaims = (token: string): WorkosClaims | null => {
  const claims = decodeJWTClaims(token)
  return typeof claims?.iss === 'string' && ALLOWED_ISSUERS.has(claims.iss) ? claims : null
}

const parseStoredSession = (raw: string | null): StoredWorkosSession | null => {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<StoredWorkosSession>
    if (typeof parsed.accessToken !== 'string' || typeof parsed.refreshToken !== 'string') return null
    return { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken }
  } catch {
    return null
  }
}

const getStoredSession = (): StoredWorkosSession | null =>
  parseStoredSession(localStorage.getItem(WORKOS_SESSION_STORAGE_KEY))

const sameStoredSession = (left: StoredWorkosSession, right: StoredWorkosSession): boolean =>
  left.accessToken === right.accessToken && left.refreshToken === right.refreshToken

const withSessionMutationLock = async <T>(operation: () => T | Promise<T>): Promise<T> =>
  navigator.locks ? navigator.locks.request(WORKOS_SESSION_MUTATION_LOCK, operation) : operation()

const writeStoredSession = (session: StoredWorkosSession): void => {
  localStorage.setItem(WORKOS_SESSION_STORAGE_KEY, JSON.stringify(session))
}

const clearStoredSession = (): void => {
  localStorage.removeItem(WORKOS_SESSION_STORAGE_KEY)
}

const toNormalizedSession = (stored: StoredWorkosSession): NormalizedAuthSession | null => {
  const claims = decodeWorkosClaims(stored.accessToken)
  const externalUserId = claims?.['urn:wispr:user_external_id']
  if (
    !claims ||
    !claims.iss ||
    !ALLOWED_ISSUERS.has(claims.iss) ||
    typeof externalUserId !== 'string' ||
    !externalUserId.trim() ||
    typeof claims.email !== 'string'
  ) {
    return null
  }

  const fullName = claims.name || [claims.given_name, claims.family_name].filter(Boolean).join(' ')
  return {
    provider: AuthTokenProvider.WorkOS,
    accessToken: stored.accessToken,
    refreshToken: stored.refreshToken,
    expiresAt: typeof claims.exp === 'number' ? claims.exp : null,
    user: {
      id: externalUserId,
      providerUserId: typeof claims.sub === 'string' ? claims.sub : null,
      email: claims.email,
      name: fullName || claims.email.split('@')[0] || 'User',
      avatarUrl: typeof claims.picture === 'string' ? claims.picture : null,
      provider: AuthTokenProvider.WorkOS,
    },
  }
}

const isNearExpiry = (session: NormalizedAuthSession): boolean =>
  session.expiresAt !== null && session.expiresAt <= Date.now() / 1000 + EXPIRY_SKEW_SECONDS

const sameIdentity = (left: NormalizedAuthSession, right: NormalizedAuthSession): boolean =>
  left.user.id === right.user.id && left.user.email === right.user.email

let refreshInFlight: Promise<RefreshResult> | null = null
const subscribers = new Set<(event: AuthStateChangeEvent, session: NormalizedAuthSession | null) => void>()

const emit = async (event: AuthStateChangeEvent): Promise<void> => {
  const session = await getSession()
  subscribers.forEach((callback) => callback(event, session))
}

const refreshStoredSession = (stored: StoredWorkosSession): Promise<RefreshResult> => {
  if (refreshInFlight) return refreshInFlight

  const refresh = async (current: StoredWorkosSession): Promise<RefreshResult> => {
    const changedSession = (): RefreshResult | null => {
      const latest = getStoredSession()
      if (!latest) return { session: null, definitiveFailure: false }
      if (sameStoredSession(latest, current)) return null
      return { session: toNormalizedSession(latest), definitiveFailure: false }
    }

    const clearCurrentSession = async (): Promise<RefreshResult> =>
      withSessionMutationLock(() => {
        const changed = changedSession()
        if (changed) return changed
        clearStoredSession()
        return { session: null, definitiveFailure: true }
      })

    const claims = decodeWorkosClaims(current.accessToken)
    const clientId = claims?.client_id
    if (typeof clientId !== 'string' || !clientId) {
      return clearCurrentSession()
    }

    // WorkOS error responses carry no CORS headers, so a rejected refresh token surfaces here as a network error.
    const giveUp = async (): Promise<RefreshResult> => {
      const expired = typeof claims.exp === 'number' && claims.exp <= Date.now() / 1000
      return expired ? clearCurrentSession() : { session: null, definitiveFailure: false }
    }

    const retryDeadline = Date.now() + REFRESH_RETRY_WINDOW_MS
    for (let attempt = 0; attempt < REFRESH_MAX_ATTEMPTS; attempt += 1) {
      const waitBeforeRetry = async (): Promise<boolean> => {
        if (attempt + 1 >= REFRESH_MAX_ATTEMPTS || Date.now() >= retryDeadline) return false
        await new Promise((resolve) => window.setTimeout(resolve, 250 * 2 ** attempt))
        return Date.now() < retryDeadline
      }

      const controller = new AbortController()
      const timeoutId = window.setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS)
      try {
        const changed = changedSession()
        if (changed) return changed

        const response = await fetch(`${claims.iss}/user_management/authenticate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            grant_type: 'refresh_token',
            client_id: clientId,
            refresh_token: current.refreshToken,
            organization_id: claims.org_id,
          }),
          signal: controller.signal,
        })

        const changedAfterResponse = changedSession()
        if (changedAfterResponse) return changedAfterResponse

        if (response.status === 408 || response.status === 429 || response.status >= 500) {
          if (await waitBeforeRetry()) continue
          return giveUp()
        }

        if (!response.ok) {
          return clearCurrentSession()
        }

        let refreshed: WorkosRefreshResponse
        try {
          refreshed = (await response.json()) as WorkosRefreshResponse
        } catch {
          const changedAfterBody = changedSession()
          if (changedAfterBody) return changedAfterBody
          if (await waitBeforeRetry()) continue
          return giveUp()
        }

        if (!refreshed.access_token || !refreshed.refresh_token) {
          return clearCurrentSession()
        }

        const next = { accessToken: refreshed.access_token, refreshToken: refreshed.refresh_token }
        const normalized = toNormalizedSession(next)
        if (!normalized) {
          return clearCurrentSession()
        }
        return withSessionMutationLock(() => {
          const changedBeforeWrite = changedSession()
          if (changedBeforeWrite) return changedBeforeWrite
          writeStoredSession(next)
          return { session: normalized, definitiveFailure: false }
        })
      } catch {
        const changedAfterFailure = changedSession()
        if (changedAfterFailure) return changedAfterFailure
        if (await waitBeforeRetry()) continue
        return giveUp()
      } finally {
        window.clearTimeout(timeoutId)
      }
    }

    return giveUp()
  }

  const runRefresh = async (): Promise<RefreshResult> => {
    const refreshCurrent = async (): Promise<RefreshResult> => {
      const current = getStoredSession()
      if (!current) return { session: null, definitiveFailure: true }
      if (!sameStoredSession(current, stored)) {
        const currentSession = toNormalizedSession(current)
        if (currentSession && !isNearExpiry(currentSession)) {
          return { session: currentSession, definitiveFailure: false }
        }
      }
      return refresh(current)
    }

    if (navigator.locks) {
      try {
        return await navigator.locks.request('admin-portal-workos-refresh', refreshCurrent)
      } catch {
        return refreshCurrent()
      }
    }
    return refreshCurrent()
  }

  refreshInFlight = runRefresh().finally(() => {
    refreshInFlight = null
  })

  return refreshInFlight
}

async function getSession(): Promise<NormalizedAuthSession | null> {
  const stored = getStoredSession()
  if (!stored) return null

  const session = toNormalizedSession(stored)
  if (!session) {
    clearStoredSession()
    return null
  }
  if (!isNearExpiry(session)) return session

  const refreshed = await refreshStoredSession(stored)
  if (refreshed.session) {
    const latest = getStoredSession()
    if (!latest) return null
    const latestSession = toNormalizedSession(latest)
    if (!latestSession) {
      clearStoredSession()
      return null
    }
    void emit(sameIdentity(session, latestSession) ? 'TOKEN_REFRESHED' : 'SIGNED_IN')
    return latestSession
  }
  if (refreshed.definitiveFailure) {
    void emit('SIGNED_OUT')
    return null
  }

  const latest = getStoredSession()
  if (!latest) return null
  const latestSession = toNormalizedSession(latest)
  if (!latestSession) clearStoredSession()
  return latestSession
}

let hasStorageListener = false
const onStorageChange = (event: StorageEvent): void => {
  if (event.key !== null && event.key !== WORKOS_SESSION_STORAGE_KEY) return
  const previous = toNormalizedSession(parseStoredSession(event.oldValue) ?? { accessToken: '', refreshToken: '' })
  const current = toNormalizedSession(getStoredSession() ?? { accessToken: '', refreshToken: '' })
  if (!current) {
    void emit('SIGNED_OUT')
    return
  }

  void emit(previous && sameIdentity(previous, current) ? 'TOKEN_REFRESHED' : 'SIGNED_IN')
}

const ensureStorageListener = (): void => {
  if (hasStorageListener) return
  window.addEventListener('storage', onStorageChange)
  hasStorageListener = true
}

const cleanupStorageListener = (): void => {
  if (subscribers.size > 0 || !hasStorageListener) return
  window.removeEventListener('storage', onStorageChange)
  hasStorageListener = false
}

export const workosAuthClient = {
  hasSession(): boolean {
    return !!getStoredSession()
  },

  getSessionSnapshot(): NormalizedAuthSession | null {
    const stored = getStoredSession()
    return stored ? toNormalizedSession(stored) : null
  },

  getSession,

  async setSession(accessToken: string, refreshToken: string): Promise<NormalizedAuthSession | null> {
    const stored = { accessToken, refreshToken }
    const session = toNormalizedSession(stored)
    if (!session) return null
    await withSessionMutationLock(() => writeStoredSession(stored))
    await emit('SIGNED_IN')
    return session
  },

  async refreshSession(): Promise<RefreshResult> {
    const stored = getStoredSession()
    if (!stored) return { session: null, definitiveFailure: true }
    const previousSession = toNormalizedSession(stored)
    const refreshed = await refreshStoredSession(stored)
    if (refreshed.session) {
      await emit(previousSession && sameIdentity(previousSession, refreshed.session) ? 'TOKEN_REFRESHED' : 'SIGNED_IN')
    }
    return refreshed
  },

  async signOut(): Promise<void> {
    await withSessionMutationLock(clearStoredSession)
    await emit('SIGNED_OUT')
  },

  async clear(): Promise<void> {
    await withSessionMutationLock(clearStoredSession)
  },

  subscribe(callback: (event: AuthStateChangeEvent, session: NormalizedAuthSession | null) => void) {
    subscribers.add(callback)
    ensureStorageListener()
    void getSession().then((session) => callback('INITIAL_SESSION', session))
    return {
      unsubscribe: () => {
        subscribers.delete(callback)
        cleanupStorageListener()
      },
    }
  },
}
